// overlayAdmin/infoTextEditor.js — progressively enhances each overlay info_text <textarea> into a
// TopoLiveEditor (contenteditable WYSIWYG, link entity), for the admin overlay list.
//
// The <textarea id="infoText<id>"> stays in the DOM (hidden) as the value / dirty / save ANCHOR — nothing
// downstream changes: OverlayAdminController reads $('#infoText'+id).val() (save), OverlayDirtyTracker
// snapshots it and toggles .fieldDirty on it (dirty). The editor keeps the textarea in sync (onChange ->
// value + a synthetic 'input' event, which the controller's delegated `.marksDirty` input listener picks
// up → recompute) and mirrors the .fieldDirty highlight onto the editor element.
//
// The live editor serializes back to the SAME "[text](href)" convention as the preview editor did, so the
// stored value + the frontend renderer (renderLinksAsAnchors) are unchanged — only the authoring UX changes.

import { createTopoLiveEditor } from "../topoLiveEditor/topoLiveEditor.js";
import { linkEntity } from "../topoEditor/entities/link.js";
import { docLinkEntity } from "../topoEditor/entities/docLink.js";
import { overlayLinkEntity } from "../topoEditor/entities/overlayLink.js";
import { createOverlayListProvider } from "../topoEditor/overlayListProvider.js";
import { str } from "../i18n/str.js";
import { makeListPicker } from "../topoEditor/pickers/listPicker.js";
import { createDisposers } from "../core/disposers.js";

export function enhanceOverlayInfoText(host) {
    const root = host || document.getElementById("overlayDataContainer");
    const bag = createDisposers();
    if (!root) { return { destroy() { bag.dispose(); } }; }

    // One overlay-list provider shared by every editor in this container (single cached get_overlays fetch).
    const overlayPicker = makeListPicker(createOverlayListProvider({ fallbackTitle: str("EDITOR_OVERLAY") }));

    root.querySelectorAll('textarea[id^="infoText"]').forEach((textarea) => {
        if (textarea.dataset.topoEditor) { return; }   // idempotent — don't double-enhance
        textarea.dataset.topoEditor = "1";

        const editor = createTopoLiveEditor({
            value: textarea.value,
            // links, doc-links {id}, overlay-links {ov:id} (picker via get_overlays)
            entities: [
                linkEntity({ labels: { title: str("EDITOR_LINK_EDIT"), button: str("EDITOR_LINK"),
                                       textField: str("EDITOR_FIELD_TEXT"), urlField: str("EDITOR_FIELD_URL") } }),
                docLinkEntity({ labels: { title: str("EDITOR_DOCLINK"), button: str("EDITOR_DOC"),
                                          idField: str("EDITOR_FIELD_DOC_ID"), tokenTitle: str("DOCUMENT") } }),
                overlayLinkEntity({ picker: overlayPicker, labels: { title: str("EDITOR_OVERLAYLINK"), button: str("EDITOR_OVERLAY"),
                                                                     idField: str("EDITOR_FIELD_OVERLAY_ID"), tokenTitle: str("EDITOR_OVERLAY") } }),
            ],
            placeholder: textarea.getAttribute("placeholder") || "",
            rows: 3,         // ~3 Zeilen bei Load, wächst wie eine Textarea
            labels: {
                close: str("CLOSE_STR"), save: str("EDITOR_SAVE"), ok: str("EDITOR_OK"), remove: str("REMOVE"),
                edit: str("EDIT"), document: str("DOCUMENT"), unpublished: str("DOC_LINK_UNPUBLISHED"),
            },
            onChange: (v) => {
                if (textarea.value === v) { return; }
                textarea.value = v;
                textarea.dispatchEvent(new Event("input", { bubbles: true }));   // → dirty tracking
            },
        });

        textarea.style.display = "none";
        textarea.parentNode.insertBefore(editor.element, textarea);

        // The dirty tracker toggles .fieldDirty on the (hidden) textarea — mirror it onto the editor so the
        // per-field "unsaved" highlight stays visible.
        const mo = new MutationObserver(() => {
            editor.element.classList.toggle("fieldDirty", textarea.classList.contains("fieldDirty"));
        });
        mo.observe(textarea, { attributes: true, attributeFilter: ["class"] });

        bag.add(() => { mo.disconnect(); editor.destroy(); });
    });

    return { destroy() { bag.dispose(); } };
}
